import {
	ADD_ALL_TO_WATCHLIST,
	ADD_TO_WATCHLIST,
	CREATE_WATCHLIST,
	DELETE_WATCHLIST,
	DOWNLOAD_WATCHLIST_SUMMARY,
	FETCH_WATCHLIST,
	FETCH_WATCHLIST_ITEM,
	FETCH_WATCHLISTS,
	FETCH_WATCHLISTS_WITH_ITEM,
	FETCH_WATCHLIST_BY_ITEM,
	REMOVE_FROM_WATCHLIST,
	SEARCH_WATCHLISTS,
	UPDATE_WATCHLIST,
	UPDATE_WATCHLIST_ITEM,
	UPDATE_WATCHLIST_ITEMS_FROM_API
} from "@/store/actions.type";
import {
	ADD_WATCHLIST,
	REMOVE_ITEM,
	REMOVE_WATCHLIST,
	REMOVED_VIRTUAL_LOG_FROM_WATCHLIST,
	REPLACE_WATCHLIST,
	REPLACE_WATCHLIST_ITEM,
	SET_EDIT_SEARCH_PARAMS,
	SET_OBJECT_TO_ADD,
	MUTATE_SNACK,
	SET_WATCHLIST,
	SET_WATCHLIST_ITEM,
	SET_WATCHLIST_SEARCH_PARAMS,
	SET_WATCHLISTS,
	UPDATE_VIRTUAL_LOG_ON_WATCHLIST,
	SET_WATCHLISTS_PAGINATION,
	SET_WATCHLISTS_FILTER
} from "@/store/mutations.type";
import {ExportService, WatchlistService} from "@/common/api";
import _without from 'lodash/without';
import {REQUEST_ERROR} from "@/common/errorcodes";
import woodtypes from "@/common/woodtypes";
import {INITIAL_WATCHLISTS_PAGINATION} from "@/common/constants";

const state = {
	watchlists: [],
	watchlistsFilter: '',
	watchlistsPagination: INITIAL_WATCHLISTS_PAGINATION,
	// currently active watchlist (in bottom bar and for auto completion)
	watchlist: null,
	// currently displayed watchlist
	displayedWatchlist: null,
	// current watchlistItem for detail view
	watchlistItem: null,
	// object pending addition to watchlist
	objectToAdd: null,
	editSearchParams: false,
	// temporary search params for dynamic watchlist preview
	watchlistSearchParams: null
};

const getters = {
	watchlists: state => state.watchlists,
	watchlistsFilter: state => state.watchlistsFilter,
	watchlistsPagination: state => state.watchlistsPagination,
	watchlist: state => state.watchlist,
	watchlistLogIds: state => {
		return state.watchlist.items.reduce((carry, item) => {
			const concreteItem = item.veneer || item.virtual || item.spliced || item.timber;
			if(!concreteItem.hasOwnProperty('logsMeta')) {
				// add single log id of a normal watchlist item
				carry.push(parseInt(concreteItem.logId));
				return carry;
			}
			// add all log ids of a virtual log
			const virtualLogIds = concreteItem.logsMeta.reduce((carry, logMeta) => {
				carry.push(parseInt(logMeta.logId));
				return carry;
			}, []);

			carry.push(...virtualLogIds);
			return carry;
		}, []);
	},
	displayedWatchlist: state => state.displayedWatchlist,
	watchlistItem: state => state.watchlistItem,
	objectToAdd: state => state.objectToAdd,
	editSearchParams: state => state.editSearchParams,
	watchlistSearchParams: state => state.watchlistSearchParams
};

const actions = {
	/**
	 * Fetches list of filtered watchlists from server
	 * @param commit
	 * @param params
	 * @returns {Promise<T | *>}
	 */
	async [FETCH_WATCHLISTS]({commit}, params) {
		return WatchlistService.get(params)
			.then(({data}) => {
				commit(SET_WATCHLISTS, data.objects);
				commit(SET_WATCHLISTS_PAGINATION, {...params.pagination, rowsNumber: data.count});
			})
			.catch(error => {
				throw new Error(error);
			})
	},
	/**
	 * Searches for watchlists matching given searchword
	 * @param commit
	 * @param params
	 * @returns {Promise<T|never>}
	 */
	async [SEARCH_WATCHLISTS]({commit}, params) {
		return WatchlistService.get({
			...params,
			pagination: INITIAL_WATCHLISTS_PAGINATION
		}).then(({data}) => {
			return data.objects || [];
		});
	},
	async [FETCH_WATCHLIST]({commit}, params) {
		return WatchlistService.find(params)
			.then(({data}) => {
				commit(SET_WATCHLIST, data.object);
			})
			.catch(error => {
				throw new Error(error);
			});
	},
	async [FETCH_WATCHLIST_ITEM]({commit}, params) {
		return WatchlistService.findItem(params)
			.then(({data}) => {
				commit(SET_WATCHLIST_ITEM, data.object);
			})
			.catch(error => {
				throw new Error(error);
			});
	},
	async [CREATE_WATCHLIST]({commit}, params) {
		return WatchlistService.create(params)
			.then(({data}) => {
				commit(MUTATE_SNACK, data.status);

				if (data.status && data.object) {
					commit(ADD_WATCHLIST, data.object);
					return data.object;
				}
			}).catch(error => {
				commit(MUTATE_SNACK, REQUEST_ERROR);
			});
	},
	async [UPDATE_WATCHLIST]({commit}, params) {
		return WatchlistService.update(params.object, params.props)
			.then(({data}) => {
				commit(MUTATE_SNACK, data.status);

				if (data.status && data.object) {
					commit(REPLACE_WATCHLIST, data.object);
				}
			}).catch(error => {
				commit(MUTATE_SNACK, REQUEST_ERROR);
			});
	},
	async [ADD_TO_WATCHLIST]({commit}, params) {
		return WatchlistService.add(params)
			.then(({data}) => {
				if (data.object) {
					commit(REPLACE_WATCHLIST, data.object);
				}
				commit(MUTATE_SNACK, data.status);
			}).catch(error => {
				commit(MUTATE_SNACK, REQUEST_ERROR);
			});
	},
	async [ADD_ALL_TO_WATCHLIST]({commit}, params) {
		return WatchlistService.merge(params)
			.then(({data}) => {
				if (data.object) {
					commit(REPLACE_WATCHLIST, data.object);
				}
				commit(MUTATE_SNACK, data.status);
			}).catch(error => {
				commit(MUTATE_SNACK, REQUEST_ERROR);
			});

	},
	async [REMOVE_FROM_WATCHLIST]({commit}, params) {
		commit(REMOVE_ITEM, params);
		WatchlistService.remove(params)
			.then(({data}) => {
				if (data.object) {
					commit(REPLACE_WATCHLIST, data.object);
				}
			}).catch(error => {
			commit(MUTATE_SNACK, REQUEST_ERROR);
		});
	},
	async [DELETE_WATCHLIST]({commit}, watchlist) {
		commit(SET_WATCHLIST, null);
		WatchlistService.delete(watchlist)
			.then(({data}) => {
				if (data.status) {
					commit(REMOVE_WATCHLIST, watchlist);
					commit(MUTATE_SNACK, data.status);
				}
			}).catch(error => {
			commit(MUTATE_SNACK, REQUEST_ERROR);
		});
	},
	async [UPDATE_WATCHLIST_ITEM]({commit}, {object, watchlist, props, hideSuccess}) {
		return WatchlistService.updateItem(object, watchlist, props)
			.then(({data}) => {
				if (data.objects) {
					commit(REPLACE_WATCHLIST_ITEM, data.objects.item);
					commit(REPLACE_WATCHLIST, data.objects.watchlist);
				}
				if (hideSuccess === undefined || hideSuccess === false) {
					commit(MUTATE_SNACK, data.status);
				}
			}).catch(error => {
				commit(MUTATE_SNACK, REQUEST_ERROR);
			});
	},
	async [FETCH_WATCHLISTS_WITH_ITEM]({commit}, logId) {
		return WatchlistService.findWatchlistsWithItem(logId)
			.then(({data}) => {
				return Object.values(data.objects);
			});
	},
	async [FETCH_WATCHLIST_BY_ITEM]({commit}, itemId) {
		return WatchlistService.findByItem(itemId)
			.then(({data}) => {
				commit(SET_WATCHLIST, data.object);
			});
	},
	[DOWNLOAD_WATCHLIST_SUMMARY]({commit}, params) {
		ExportService.downloadWatchlistSummary(params);
	}
};

const mutations = {
	[SET_WATCHLIST_SEARCH_PARAMS](state, params) {
		state.watchlistSearchParams = params;
	},
	[SET_OBJECT_TO_ADD](state, object) {
		state.objectToAdd = object;
	},
	[SET_WATCHLISTS](state, watchlists) {
		state.watchlists = watchlists;
	},
	[SET_WATCHLISTS_PAGINATION](state, pagination) {
		state.watchlistsPagination = pagination;
	},
	[SET_WATCHLISTS_FILTER](state, filter) {
		state.watchlistsFilter = filter;
	},
	[SET_WATCHLIST_ITEM](state, watchlistItem) {
		state.watchlistItem = watchlistItem;
	},
	[ADD_WATCHLIST](state, watchlist) {
		state.watchlists.unshift(watchlist);
	},
	[SET_WATCHLIST](state, watchlist) {
		if (watchlist === null || (state.watchlist && state.watchlist.uid !== watchlist.uid)) {
			// todo clear dynamic watchlist search when switching watchlists

		}
		state.watchlist = watchlist;
		state.displayedWatchlist = watchlist;
	},
	[REPLACE_WATCHLIST](state, watchlist) {
		for (let i in state.watchlists) {
			if (state.watchlists.hasOwnProperty(i) && state.watchlists[i].uid === watchlist.uid) {
				state.watchlists.splice(i, 1, watchlist);
			}
		}

		state.watchlist = watchlist;
		if (state.displayedWatchlist && state.watchlist.uid === state.displayedWatchlist.uid) {
			state.displayedWatchlist = watchlist;
		}
	},
	[REMOVE_WATCHLIST](state, watchlist) {
		for (let i in state.watchlists) {
			if (state.watchlists.hasOwnProperty(i) && state.watchlists[i].uid === watchlist.uid) {
				state.watchlists.splice(i, 1);
			}
		}
	},
	[REPLACE_WATCHLIST_ITEM](state, item) {
		if (state.watchlists) {
			for (let j in state.watchlists) {
				if (state.watchlists.hasOwnProperty(j)) {
					for (let i in state.watchlists[j].items) {
						if (state.watchlists[j].items.hasOwnProperty(i) && state.watchlists[j].items[i].itemType === item.itemType && state.watchlists[j].items[i].uid === item.uid) {
							state.watchlists[j].items.splice(i, 1, item);
						}
					}
				}
			}
		}

		if (state.watchlist) {
			for (let i in state.watchlist.items) {
				if (state.watchlist.items.hasOwnProperty(i) && state.watchlist.items[i].itemType === item.itemType && state.watchlist.items[i].uid === item.uid) {
					state.watchlist.items.splice(i, 1, item);
				}
			}
		}
	},
	[UPDATE_VIRTUAL_LOG_ON_WATCHLIST](state, log) {
		if (state.watchlist) {
			for (let i in state.watchlist.items) {
				if (state.watchlist.items.hasOwnProperty(i) &&
					state.watchlist.items[i].itemType === woodtypes.VIRTUAL &&
					state.watchlist.items[i].virtual.uid === log.uid
				) {
					state.watchlist.items[i].virtual = log;
				}
			}
		}
	},
	[REMOVE_ITEM](state, params) {
		if (params.item.hasOwnProperty('itemType') && params.item.itemType === woodtypes.VIRTUAL) {
			this.commit(REMOVED_VIRTUAL_LOG_FROM_WATCHLIST, params.item);
		}
		state.watchlist.items = _without(state.watchlist.items, params.item);
	},
	[SET_EDIT_SEARCH_PARAMS](state, edit) {
		state.editSearchParams = edit;
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};